import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckSquare, Square, Wrench, AlertTriangle } from 'lucide-react';
import './GuidePage.css';

export default function BikeCheckChecklist() {
  const [checkedItems, setCheckedItems] = useState({});

  const toggleItem = (id) => {
    setCheckedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const categories = [
    {
      name: 'Reifen & Räder (KRITISCH!)',
      priority: 'high',
      items: [
        { id: 'tire1', text: 'Reifendruck prüfen (empfohlener PSI/Bar auf Reifen)' },
        { id: 'tire2', text: 'Reifen auf Risse, Schnitte, Abnutzung prüfen' },
        { id: 'tire3', text: 'Profiltiefe prüfen (mind. 1-2mm)' },
        { id: 'tire4', text: 'Seitenwände auf Beschädigungen prüfen' },
        { id: 'tire5', text: 'Ventile auf Dichtigkeit prüfen (Seifenwasser-Test)' },
        { id: 'tire6', text: 'Felgen auf Dellen, Risse prüfen' },
        { id: 'tire7', text: 'Speichen auf Spannung prüfen (keine lockeren!)' },
        { id: 'tire8', text: 'Laufräder zentriert? (Rad drehen, Schlag prüfen)' }
      ]
    },
    {
      name: 'Bremsen (LEBENSNOTWENDIG!)',
      priority: 'high',
      items: [
        { id: 'brake1', text: 'Bremsbeläge prüfen (mind. 1-2mm Material übrig)' },
        { id: 'brake2', text: 'Bremsscheiben auf Verschleiß prüfen (falls Disc Brakes)' },
        { id: 'brake3', text: 'Bremszüge/Hydraulik auf Rost, Risse prüfen' },
        { id: 'brake4', text: 'Bremshebel-Gefühl testen (fester Druckpunkt?)' },
        { id: 'brake5', text: 'Bremsen justieren (gleichmäßig, kein Schleifen)' },
        { id: 'brake6', text: 'Testfahrt: Bremsen voll ziehen (stoppt sofort?)' }
      ]
    },
    {
      name: 'Schaltung',
      priority: 'medium',
      items: [
        { id: 'shift1', text: 'Alle Gänge durchschalten (sauber & ohne Springen?)' },
        { id: 'shift2', text: 'Schaltzüge auf Rost, Fransen prüfen' },
        { id: 'shift3', text: 'Schaltwerk/Umwerfer auf Beschädigungen prüfen' },
        { id: 'shift4', text: 'Schaltwerk-Einstellung prüfen (H/L Limits OK?)' },
        { id: 'shift5', text: 'Unter Last schalten testen (bergauf simulieren)' }
      ]
    },
    {
      name: 'Antrieb (Kette, Kassette, Kurbel)',
      priority: 'medium',
      items: [
        { id: 'drive1', text: 'Kette auf Verschleiß prüfen (Kettenlehre nutzen!)' },
        { id: 'drive2', text: 'Kette reinigen & ölen' },
        { id: 'drive3', text: 'Kassette auf abgenutzte Zähne prüfen (Haifischzähne = verschlissen)' },
        { id: 'drive4', text: 'Kettenblätter auf Verschleiß prüfen' },
        { id: 'drive5', text: 'Kurbel auf Spiel prüfen (fest sitzen?)' },
        { id: 'drive6', text: 'Pedal auf Spiel & Drehung prüfen' }
      ]
    },
    {
      name: 'Laufräder & Naben',
      priority: 'medium',
      items: [
        { id: 'hub1', text: 'Vorderrad: Achse fest? (keine Bewegung seitlich)' },
        { id: 'hub2', text: 'Hinterrad: Achse fest?' },
        { id: 'hub3', text: 'Schnellspanner/Steckachse richtig geschlossen?' },
        { id: 'hub4', text: 'Naben auf Spiel prüfen (Rad anheben & wackeln)' },
        { id: 'hub5', text: 'Naben auf Laufgeräusche prüfen (sollte smooth laufen)' }
      ]
    },
    {
      name: 'Lenker & Vorbau',
      priority: 'high',
      items: [
        { id: 'stem1', text: 'Lenker fest am Vorbau? (keine Bewegung!)' },
        { id: 'stem2', text: 'Vorbau fest am Gabelschaft? (Bremse ziehen & wackeln)' },
        { id: 'stem3', text: 'Lenker auf Risse prüfen (besonders Carbon!)' },
        { id: 'stem4', text: 'Lenkergriffe fest? (keine Drehung)' },
        { id: 'stem5', text: 'Lenkerband/Grips in gutem Zustand?' }
      ]
    },
    {
      name: 'Sattel & Sattelstütze',
      priority: 'medium',
      items: [
        { id: 'saddle1', text: 'Sattel fest auf Sattelstütze? (Klemmung OK)' },
        { id: 'saddle2', text: 'Sattelstütze fest im Rahmen? (keine Absenkung)' },
        { id: 'saddle3', text: 'Sattel waagerecht? (mit Wasserwaage prüfen)' },
        { id: 'saddle4', text: 'Sattelhöhe korrekt? (Bein fast gestreckt bei Pedal unten)' }
      ]
    },
    {
      name: 'Gabel & Dämpfer (falls vorhanden)',
      priority: 'medium',
      items: [
        { id: 'fork1', text: 'Gabel auf Spiel prüfen (Bremse ziehen & vor-zurück wackeln)' },
        { id: 'fork2', text: 'Federgabel: Luftdruck prüfen (empfohlener PSI)' },
        { id: 'fork3', text: 'Dämpfer: Luftdruck prüfen (falls Full-Suspension)' },
        { id: 'fork4', text: 'Standrohre auf Kratzer, Öl-Lecks prüfen' }
      ]
    },
    {
      name: 'Beleuchtung (PFLICHT in UK bei Dunkelheit!)',
      priority: 'high',
      items: [
        { id: 'light1', text: 'Frontlicht funktioniert? (mind. 300 Lumen)' },
        { id: 'light2', text: 'Rücklicht funktioniert? (Blink- & Dauerlicht)' },
        { id: 'light3', text: 'Batterien voll geladen / frisch?' },
        { id: 'light4', text: 'Halterungen fest montiert?' },
        { id: 'light5', text: 'Reflektoren vorhanden? (Pedale, Speichen, hinten)' }
      ]
    },
    {
      name: 'E-Bike Spezifisch',
      priority: 'high',
      items: [
        { id: 'ebike1', text: 'Akku voll geladen?' },
        { id: 'ebike2', text: 'Akku fest im Rahmen?' },
        { id: 'ebike3', text: 'Display funktioniert? (alle Modi testbar?)' },
        { id: 'ebike4', text: 'Motor macht keine ungewöhnlichen Geräusche?' },
        { id: 'ebike5', text: 'Alle Unterstützungsstufen testen (Eco/Tour/Turbo)' },
        { id: 'ebike6', text: 'Kabel & Stecker auf Beschädigungen prüfen' },
        { id: 'ebike7', text: 'Ladegerät & UK-Adapter eingepackt?' }
      ]
    },
    {
      name: 'Sonstiges',
      priority: 'medium',
      items: [
        { id: 'misc1', text: 'Klingel/Hupe funktioniert?' },
        { id: 'misc2', text: 'Flaschenhalter fest montiert?' },
        { id: 'misc3', text: 'Gepäckträger fest? (keine lockeren Schrauben)' },
        { id: 'misc4', text: 'Schutzbleche richtig montiert? (kein Schleifen)' },
        { id: 'misc5', text: 'Ständer funktioniert? (falls vorhanden)' }
      ]
    },
    {
      name: 'Testfahrt (MUSS gemacht werden!)',
      priority: 'high',
      items: [
        { id: 'test1', text: '5-10km Testfahrt gemacht (vor Abreise!)' },
        { id: 'test2', text: 'Anstiege getestet (Schaltung unter Last)' },
        { id: 'test3', text: 'Abfahrten getestet (Bremsen!)' },
        { id: 'test4', text: 'Keine ungewöhnlichen Geräusche?' },
        { id: 'test5', text: 'Alles fühlt sich sicher an?' }
      ]
    }
  ];

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalCount = categories.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🔧 Bike-Check Checkliste</h1>
        
        <div className="info-box">
          <p>
            <strong>Interaktive Checkliste:</strong> Prüfe dein Rad vor der Schottland-Tour! 
            Fortschritt: <strong>{checkedCount} / {totalCount}</strong> Punkte erledigt.
          </p>
        </div>

        <div className="warning-box">
          <AlertTriangle size={24} />
          <div>
            <strong>WICHTIG: Testfahrt 1-2 Wochen vor Abreise!</strong><br />
            Finde Probleme JETZT, nicht in den schottischen Highlands wo der nächste 
            Bike-Shop 80km entfernt ist!
          </div>
        </div>

        <div className="pro-tip" style={{ marginBottom: '30px' }}>
          <h3><Wrench size={20} /> Wann zum Profi?</h3>
          <ul>
            <li><strong>Wenn du unsicher bist:</strong> Bike-Shop! (£50-80 für Full-Service)</li>
            <li><strong>Kettenverschleiß:</strong> Kettenlehre nutzen (oder Shop fragen)</li>
            <li><strong>Bremsen quietschen:</strong> Beläge nachstellen/tauschen lassen</li>
            <li><strong>Schaltung springt:</strong> Einstellen lassen (kompliziert!)</li>
            <li><strong>E-Bike Fehler:</strong> Nur Fachhändler! (Elektronik = kompliziert)</li>
          </ul>
        </div>

        {categories.map((category, idx) => (
          <section key={idx}>
            <h2>
              {category.name}
              {category.priority === 'high' && ' ⚠️ KRITISCH'}
            </h2>
            <div className="checklist">
              {category.items.map(item => (
                <div 
                  key={item.id} 
                  className="check-item" 
                  onClick={() => toggleItem(item.id)}
                  style={{ 
                    cursor: 'pointer', 
                    opacity: checkedItems[item.id] ? 0.6 : 1,
                    textDecoration: checkedItems[item.id] ? 'line-through' : 'none'
                  }}
                >
                  {checkedItems[item.id] ? <CheckSquare size={20} /> : <Square size={20} />}
                  {item.text}
                </div>
              ))}
            </div>
          </section>
        ))}

        <section>
          <h2>Zeitplan: Wann was checken?</h2>
          
          <h3>🗓️ 2-4 Wochen vor Abreise</h3>
          <ul>
            <li><strong>Full Bike-Service:</strong> Shop-Besuch oder selbst kompletter Check</li>
            <li><strong>Verschleißteile tauschen:</strong> Kette, Bremsbeläge, Reifen (falls nötig)</li>
            <li><strong>Testfahrt 1:</strong> 30-50km mit Gepäck</li>
          </ul>

          <h3>🗓️ 1 Woche vor Abreise</h3>
          <ul>
            <li><strong>Letzte Checks:</strong> Nochmal alles durchgehen</li>
            <li><strong>Testfahrt 2:</strong> 20-30km (finale Bestätigung)</li>
            <li><strong>Ersatzteile kaufen:</strong> Schläuche, Bremsbeläge (falls nötig)</li>
          </ul>

          <h3>🗓️ 1 Tag vor Abreise</h3>
          <ul>
            <li><strong>Schneller Check:</strong> Reifendruck, Bremsen, Lichter</li>
            <li><strong>E-Bike:</strong> Akku voll laden (100%)</li>
            <li><strong>Rad einpacken:</strong> Für Transport vorbereiten</li>
          </ul>
        </section>

        <section>
          <h2>Häufige Probleme & Lösungen</h2>
          
          <h3>❌ Problem: Kette springt unter Last</h3>
          <ul>
            <li><strong>Ursache:</strong> Verschlissene Kette oder Kassette</li>
            <li><strong>Lösung:</strong> Kettenlehre nutzen, ggf. tauschen (Kette £15-30)</li>
            <li><strong>Notlösung:</strong> Nicht die großen/kleinen Gänge nutzen</li>
          </ul>

          <h3>❌ Problem: Bremsen quietschen/schleifen</h3>
          <ul>
            <li><strong>Ursache:</strong> Schmutz, verglaste Beläge, falsche Einstellung</li>
            <li><strong>Lösung:</strong> Beläge reinigen/schleifen, Bremse justieren</li>
            <li><strong>Profi-Tip:</strong> Zum Shop (schwer selbst zu fixen!)</li>
          </ul>

          <h3>❌ Problem: Schaltung trifft Gänge nicht sauber</h3>
          <ul>
            <li><strong>Ursache:</strong> Schaltzug gedehnt, falsche Einstellung</li>
            <li><strong>Lösung:</strong> Barrel Adjuster drehen (kleine Einstellungen)</li>
            <li><strong>Profi-Tip:</strong> Zum Shop für komplette Einstellung</li>
          </ul>

          <h3>❌ Problem: Reifen verliert langsam Luft</h3>
          <ul>
            <li><strong>Ursache:</strong> Kleines Loch, undichtes Ventil</li>
            <li><strong>Lösung:</strong> Schlauch rausnehmen, Seifenwasser-Test, flicken/tauschen</li>
            <li><strong>Wichtig:</strong> VOR der Tour fixen! (nervt sonst täglich)</li>
          </ul>
        </section>

        <div className="warning-box">
          <h3>⚠️ Top 5 Gründe für Tour-Abbruch (vermeide sie!)</h3>
          <ol>
            <li><strong>Platten wegen abgefahrenen Reifen</strong> → NEUE REIFEN!</li>
            <li><strong>Kette gerissen</strong> → Kettenverschleiß vorher prüfen!</li>
            <li><strong>Bremsen versagen</strong> → Beläge vorher checken/tauschen!</li>
            <li><strong>E-Bike Akku defekt</strong> → Vorher testen, Ersatz mitnehmen</li>
            <li><strong>Speichenbruch bei schlechten Laufrädern</strong> → Laufräder-Check!</li>
          </ol>
        </div>

        <div className="info-box">
          <h4>📚 Weiterführende Guides</h4>
          <ul>
            <li><Link to="/wissen/ausruestung">🎒 Ausrüstungs-Guide</Link> - Werkzeug für unterwegs</li>
            <li><Link to="/wissen/ebike">⚡ E-Bike Basics</Link> - E-Bike spezifische Wartung</li>
            <li><Link to="/wissen/checklist/packlist">✅ Packliste</Link> - Was mitnehmen</li>
          </ul>
        </div>

        <div className="download-section">
          <h3>📥 Checkliste als PDF speichern</h3>
          <p>Drucke die Bike-Check Checkliste aus oder speichere sie als PDF!</p>
          <button className="download-btn" onClick={() => window.print()}>
            Als PDF speichern/drucken
          </button>
        </div>
      </article>
    </div>
  );
}
